import { useContext, useEffect, useState } from "react";

import {
  Button,
  Card,
  Container,
  Divider,
  Modal,
  Notification,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";

import { useForm } from "@mantine/form";
import { IconKey, IconCoin, IconGift } from "@tabler/icons-react";

import dayjs from "dayjs";

import MegaLoader from "../../Components/MegaLoader";
import LoyaltyHistoryModal from "../../Components/Loyalty/LoyaltyHistoryModal";
import { AppContext } from "../Wrapper";

import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  ShowValidationError,
  trimString,
  validateEmail,
} from "../../Lib/Methods";
import { AppName, Colors } from "../../Lib/Data";
import { PerformRequest } from "../../Lib/usePerformRequest";
import { Endpoints } from "../../Lib/Endpoints";
import { useDisclosure } from "@mantine/hooks";

import "./styles.scss";

const ProfilePage: React.FC = () => {
  const context = useContext(AppContext);
  const theme = context?.theme ?? "light";

  document.title = `My Profile | ${AppName}`;

  const [isUpdatingProfile, setUpdatingProfile] = useState<boolean>(false);
  const [isUpdatingEmail, setUpdatingEmail] = useState<boolean>(false);

  const [isUpdatingPassword, setUpdatingPassword] = useState<boolean>(false);
  const [isUpdatingOtp, setUpdatingOtp] = useState<boolean>(false);
  const [
    isPasswordModalOpened,
    { open: OpenPasswordModal, close: ClosePasswordModal },
  ] = useDisclosure(false);

  const [
    isLoyaltyModalOpened,
    { open: OpenLoyaltyModal, close: CloseLoyaltyModal },
  ] = useDisclosure(false);

  const [isRedeemingPoints, setRedeemingPoints] = useState(false);

  const profileForm = useForm({
    initialValues: {
      name: "",
    },
    validate: {
      name: (value) =>
        value && value.length > 2 ? null : "Please enter your name",
    },
  });
  const emailForm = useForm({
    initialValues: {
      email: "",
    },
    validate: {
      email: (value) =>
        validateEmail(value) ? null : "Please enter a valid email",
    },
  });

  const UpdateProfile = async () => {
    const { hasErrors } = profileForm.validate();
    if (!hasErrors) {
      setUpdatingProfile(true);
      const r = await PerformRequest({
        method: "PATCH",
        route: Endpoints.GetUpdateProfile(),
        data: {
          name: trimString(profileForm.values.name),
        },
        callback: () => {
          setUpdatingProfile(false);
        },
      });
      setUpdatingProfile(false);
      if (r) {
        if (r.status === 200) {
          DefaultSuccessNotification("Profile updated!");
        }
      }
    }
  };
  const UpdateEmail = async () => {
    const { hasErrors } = emailForm.validate();
    if (!hasErrors) {
      setUpdatingEmail(true);
      const r = await PerformRequest({
        method: "PATCH",
        route: Endpoints.GetUpdateEmail(),
        data: {
          email: trimString(emailForm.values.email, true),
        },
        callback: () => {
          setUpdatingEmail(false);
        },
      });
      setUpdatingEmail(false);
      if (r) {
        if (r.status === 200) {
          DefaultSuccessNotification("Profile updated!");
        } else if (r.status === 409) {
          DefaultErrorNotification("Email already taken by another account!");
        }
      }
    }
  };

  const SendPasswordResetLink = async () => {
    setUpdatingPassword(true);
    const r = await PerformRequest({
      method: "POST",
      route: Endpoints.GetSendPasswordResetLink(),
      data: {
        email: trimString(context?.profile?.email as string, true),
      },
      callback: () => {
        setUpdatingPassword(false);
      },
    });
    setUpdatingPassword(false);
    if (r) {
      if (r.status === 200) {
        DefaultSuccessNotification("Password reset link sent!");
      }
    }
  };

  const ToggleRequireOtp = async () => {
    setUpdatingOtp(true);
    const r = await PerformRequest({
      method: "PATCH",
      route: Endpoints.GetToggleRequireOtp(),
      data: {
        status: !context?.profile?.requireOTP,
      },
      callback: () => {
        setUpdatingOtp(false);
      },
    });
    setUpdatingOtp(false);
    if (r) {
      if (r.status === 200) {
        DefaultSuccessNotification("OTP requirement updated!");
        context?.reloadProfile();
      } else {
        DefaultErrorNotification("An error occurred!");
      }
    } else {
      DefaultErrorNotification("An error occurred!");
    }
  };

  const RedeemPoints = async () => {
    setRedeemingPoints(true);
    const r = await PerformRequest({
      method: "POST",
      route: Endpoints.GetRedeemLoyaltyPoints(),
      data: {},
      callback: () => setRedeemingPoints(false),
    });
    setRedeemingPoints(false);

    if (r) {
      if (r.status === 200) {
        DefaultSuccessNotification("Coupon generated successfully!");
        context?.reloadProfile();

        // Show the coupon in a notification or modal
        const coupon = (r.data as any).coupon;
        if (coupon) {
          //Ideally we show this in a nice modal, but for now a persistent notification
          import("@mantine/notifications").then(({ notifications }) => {
            notifications.show({
              title: "Points Redeemed!",
              message: `Your coupon code is: ${coupon}. Copy it now!`,
              color: "green",
              autoClose: false,
            });
          });
        }
      } else {
        DefaultErrorNotification(
          (r.data as any).message || "Failed to redeem points"
        );
      }
    }
  };

  useEffect(() => {
    if (context && context.profile) {
      const { email, name } = context.profile;
      profileForm.setFieldValue("name", name);
      emailForm.setFieldValue("email", email);
    }
  }, [context]);

  const inputProps = {
    styles: {
      label: {
        color:
          theme === "dark" ? Colors.WhiteBlueTransparent : Colors.BlackPrimary,
      },
    },
    spellCheck: false,
    w: 240,
  };

  return (
    <Container size="md">
      <div className="profile-container">
        <div className="profile-header flex-col">
          <Text
            className="title"
            c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
          >
            My Profile
          </Text>
          <Text
            className="subtitle"
            c={theme === "dark" ? Colors.GraySecondary : Colors.GrayPrimary}
          >
            Manage your personal information and security settings
          </Text>
        </div>

        {!context || context.isLoadingProfile ? (
          <MegaLoader />
        ) : (
          <div className={`profile-card profile-card-${theme}`}>
            <div className="info-grid">
              <div className="field-group">
                <Text
                  c={
                    theme === "dark"
                      ? Colors.WhiteBlueTransparent
                      : Colors.BlackPrimary
                  }
                  className="section-title"
                >
                  Personal Information
                </Text>
                <div className="flex-col" style={{ gap: 20 }}>
                  <div className="input-wrapper">
                    <TextInput
                      label="Full Name"
                      placeholder="Enter full name"
                      {...profileForm.getInputProps("name")}
                      {...inputProps}
                      w="100%"
                    />
                    <Button
                      mt={26}
                      disabled={
                        context.profile?.name === profileForm.values.name
                      }
                      onClick={UpdateProfile}
                      loading={isUpdatingProfile}
                      variant="light"
                      color={Colors.BluePrimary}
                    >
                      Save
                    </Button>
                  </div>
                  <div className="input-wrapper">
                    <TextInput
                      label="Email Address"
                      placeholder="you@email.com"
                      {...emailForm.getInputProps("email")}
                      {...inputProps}
                      w="100%"
                    />
                    <Button
                      mt={26}
                      disabled={
                        context.profile?.email === emailForm.values.email
                      }
                      onClick={UpdateEmail}
                      loading={isUpdatingEmail}
                      variant="light"
                      color={Colors.BluePrimary}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </div>

              <div className="field-group">
                <Text
                  c={
                    theme === "dark"
                      ? Colors.WhiteBlueTransparent
                      : Colors.BlackPrimary
                  }
                  className="section-title"
                >
                  Account Details
                </Text>
                <div className="flex-col" style={{ gap: 15 }}>
                  <div>
                    <Text fz="sm" c="dimmed" mb={4}>
                      Date Joined
                    </Text>
                    <Text fw={600} c={Colors.GreenSecondary}>
                      {dayjs(context.profile?.createdAt).format("MMMM D, YYYY")}
                    </Text>
                  </div>
                </div>
              </div>

              <Divider my="xl" />

              <div className="field-group">
                <Text
                  c={
                    theme === "dark"
                      ? Colors.WhiteBlueTransparent
                      : Colors.BlackPrimary
                  }
                  className="section-title"
                >
                  Loyalty Points
                </Text>
                <div
                  className="flex-row justify-between align-center"
                  style={{ marginTop: 10 }}
                >
                  <div className="flex-col">
                    <Text fz="sm" c="dimmed">
                      Current Balance
                    </Text>
                    <Text fw={700} fz={24} c={Colors.OrangePrimary}>
                      {context.profile?.points || 0} Points
                    </Text>
                  </div>

                  <div className="flex-row" style={{ gap: 10 }}>
                    <Button
                      variant="light"
                      color="orange"
                      onClick={OpenLoyaltyModal}
                      leftSection={<IconCoin size={18} />}
                    >
                      History
                    </Button>
                    <Button
                      variant="filled"
                      color="orange"
                      loading={isRedeemingPoints}
                      onClick={RedeemPoints}
                      disabled={(context.profile?.points || 0) < 100} // Assuming 100 is min
                      leftSection={<IconGift size={18} />}
                    >
                      Redeem
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <Divider my="xl" />

            <div className="security-section">
              <Text
                c={
                  theme === "dark"
                    ? Colors.WhiteBlueTransparent
                    : Colors.BlackPrimary
                }
                className="section-title"
              >
                Security & Preferences
              </Text>

              <div className="flex-col" style={{ gap: 20 }}>
                {context.profile?.isVerified && (
                  <Card
                    withBorder
                    padding="lg"
                    radius="md"
                    bg={
                      theme === "dark"
                        ? "rgba(0,0,0,0.2)"
                        : "var(--mantine-color-gray-0)"
                    }
                    style={{ borderColor: "var(--mantine-color-gray-2)" }}
                  >
                    <div className="flex-row justify-between align-center">
                      <div style={{ flex: 1, paddingRight: 20 }}>
                        <Text fw={600} mb={4}>
                          Two-Factor Authentication (OTP)
                        </Text>
                        <Text fz="sm" c="dimmed">
                          If enabled, you will be required to enter a one-time
                          password sent to your email when logging in from a new
                          device.
                        </Text>
                      </div>
                      <Switch
                        disabled={isUpdatingOtp}
                        checked={context.profile?.requireOTP}
                        onClick={ToggleRequireOtp}
                        size="md"
                        color={Colors.GreenPrimary}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  </Card>
                )}

                <div className="flex-row">
                  <Button
                    variant="outline"
                    color={Colors.GreenPrimary}
                    onClick={OpenPasswordModal}
                    leftSection={<IconKey size={18} />}
                  >
                    Change Password
                  </Button>
                </div>
              </div>
            </div>

            {context.profile?.email !== emailForm.values.email && (
              <Notification
                color={Colors.OrangePrimary}
                mt="md"
                title="Verify New Email"
                withCloseButton={false}
              >
                Changing your email to {emailForm.values.email} will require you
                to verify ownership of the new address.
              </Notification>
            )}

            <Modal
              centered
              opened={isPasswordModalOpened}
              onClose={ClosePasswordModal}
              title="Reset Password"
              radius="lg"
            >
              <Text c="dimmed" fz={14} fw={500} mb={20}>
                We will send a password reset link to{" "}
                <b style={{ color: Colors.BluePrimary }}>
                  {context.profile?.email}
                </b>
                . Click the link in the email to set a new password.
              </Text>
              <Button
                fullWidth
                leftSection={<IconKey size={18} />}
                color={Colors.GreenPrimary}
                onClick={SendPasswordResetLink}
                loading={isUpdatingPassword}
              >
                Send Reset Link
              </Button>
              <Button
                fullWidth
                leftSection={<IconKey size={18} />}
                color={Colors.GreenPrimary}
                onClick={SendPasswordResetLink}
                loading={isUpdatingPassword}
              >
                Send Reset Link
              </Button>
            </Modal>

            <LoyaltyHistoryModal
              opened={isLoyaltyModalOpened}
              onClose={CloseLoyaltyModal}
              theme={theme}
            />
          </div>
        )}
      </div>
    </Container>
  );
};

export default ProfilePage;
